import { useState, useEffect, useCallback } from 'react';
import { fetchLocations } from '../api/locationApi';
const STORAGE_KEY = 'pondlogs_selected_location_id';
// ── Bridge to @eruvaka/web-pondlogs-ponds ──────────────────────────────────
// The ponds package listens for this key + event to reactively reload ponds
// data and re-centre maps whenever the active farm changes. Any future package
// that also subscribes to 'ponds:location-changed' will react automatically.
const PONDS_LOCATION_KEY = 'ponds_active_location_id';
const PONDS_LOCATION_EVENT = 'ponds:location-changed';
const syncToPondsPackage = (locationId) => {
    try {
        if (locationId) {
            localStorage.setItem(PONDS_LOCATION_KEY, locationId);
        }
        else {
            localStorage.removeItem(PONDS_LOCATION_KEY);
        }
    }
    catch {
        // ignore storage errors
    }
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(PONDS_LOCATION_EVENT, { detail: locationId }));
    }
};
export const useLocations = () => {
    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocationState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchLocations();
            setLocations(data);
            // Restore previously selected location, or fall back to first
            const savedId = localStorage.getItem(STORAGE_KEY);
            const restored = data.find((l) => l.locationId === savedId) ?? data[0] ?? null;
            setSelectedLocationState(restored);
            if (restored) {
                localStorage.setItem(STORAGE_KEY, restored.locationId);
                syncToPondsPackage(restored.locationId);
            }
            else {
                localStorage.removeItem(STORAGE_KEY);
                syncToPondsPackage(null);
            }
        }
        catch (err) {
            setError('errors.loadLocationsFailed');
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        void load();
    }, [load]);
    // Sync with location changes triggered by other useLocations instances (e.g. FarmSwitcher).
    // FarmSwitcher dispatches PONDS_LOCATION_EVENT whenever the user picks a new farm.
    // Without this listener every other component calling useLocations() has stale state.
    useEffect(() => {
        const handler = (e) => {
            const locationId = e.detail;
            setSelectedLocationState((prev) => {
                // Already in sync — avoid a re-render
                if (prev?.locationId === locationId)
                    return prev;
                if (!locationId)
                    return null;
                // Match against the loaded locations list
                return locations.find((l) => l.locationId === locationId) ?? prev;
            });
        };
        window.addEventListener(PONDS_LOCATION_EVENT, handler);
        return () => window.removeEventListener(PONDS_LOCATION_EVENT, handler);
    }, [locations]);
    const setSelectedLocation = useCallback((loc) => {
        setSelectedLocationState(loc);
        localStorage.setItem(STORAGE_KEY, loc.locationId);
        syncToPondsPackage(loc.locationId);
    }, []);
    return { locations, selectedLocation, setSelectedLocation, loading, error, reload: load };
};
